import { PropsWithChildren, ReactNode, useState } from 'react'

import { widgetParams } from 'aem-libs-api/src/services/widgetParams/v1'
import { useAppContext } from 'aem-libs-react-ts/src/hooks/useAppContext'
import { useMount } from 'aem-libs-react-ts/src/hooks/useMount'
import { StaticParamsBase } from 'aem-libs-react-ts/src/types/common'
import { getConfig } from 'aem-libs-react-ts/src/utils/getConfig'
import { Maybe } from 'aem-libs-ts/src/types/utility'
import { pipe } from 'aem-libs-ts/src/utils/common/pipe'

import { ParamsFunctionResolver, ParseError, ParseSuccess, isErrorResult } from './parseDynamicParams'


interface ConfigWrapperProps<TDynamicParams> {
  renderError: () => ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parseDynamicParams?: ParamsFunctionResolver
  onFetchParams?: Maybe<(params: TDynamicParams, paramsStatic: StaticParamsBase) => void>
}

const widgetsApi = widgetParams(getConfig())

async function initParams<TParamsDynamic>(instanceId: number, onError: () => void) {
  try {
    const response = await widgetsApi.getWidgetInstanceParams<TParamsDynamic>({ instanceId })
    return response.data
  } catch {
    onError()
  }
}

export function ConfigWrapper<TDynamicParams>({
  children,
  renderError,
  parseDynamicParams,
  onFetchParams
}: PropsWithChildren<ConfigWrapperProps<TDynamicParams>>) {
  const [resolved, setResolved] = useState(false)
  const { paramsStatic, setError, setParamsDynamic, appSpinner, error, appName } = useAppContext<
    StaticParamsBase,
    TDynamicParams
  >()

  function defaultParamsParse<T>(data: T): ParseSuccess<T> {
    return {
      result: 'success',
      params: data
    }
  }

  function resolveParams(params: TDynamicParams) {
    const parseParams = parseDynamicParams ?? defaultParamsParse
    return pipe(() => params)
      .map(parseParams)
      .throwWhen(isErrorResult, (parsedParams) => parsedParams as ParseError)
      .map((parsedParams) => (parsedParams as ParseSuccess<TDynamicParams>).params)
      .tap((params) => {
        if (onFetchParams) {
          onFetchParams(params, paramsStatic)
        }
        setParamsDynamic(() => params)
        appSpinner.hide()
        setResolved(true)
        return params
      })
      .getOrElse((e) => {
        console.error(`${appName} (instanceId: ${paramsStatic.instanceId}) params errors:`)
        console.error(e.errors)
        setError()
        appSpinner.hide()
        throw new Error(JSON.stringify(e))
      })
  }

  useMount(async () => {
    if (paramsStatic.instanceId && !paramsStatic.disableFetchParams) {
      const dynamicParams = await initParams<TDynamicParams>(paramsStatic.instanceId, setError)
      resolveParams(dynamicParams as TDynamicParams)
      return
    }

    appSpinner.hide()
    setResolved(true)
  })

  if (error) {
    return renderError()
  }

  return resolved ? <>{children}</> : <></>
}
